import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { ApiError, type BulkInviteResult, type CreateOrgResult } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { EmailChipsInput, EMAIL_RE, splitEmails } from "../components/EmailChipsInput";
import { isAcceptableDomain } from "../lib/domain";
import { generatePassword } from "../lib/password";
import { CopyableId } from "../components/CopyableId";

/**
 * Sales-assisted signup: create an organization for a customer instead of making them
 * self-register (which they routinely get wrong — every employee registering their own org).
 * Creates the org with its first Administrator (generated password, shown once for hand-off),
 * force-verifies their email, then bulk-invites the rest of the team and optionally locks the
 * email domain so stragglers can't self-register new orgs.
 */
export function CreateOrganizationPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "organizations", label: "Organizations", active: false, onSelect: () => navigate("/support/organizations") },
    { id: "invitations", label: "Invitations", active: false, onSelect: () => navigate("/support/organization-invitations") },
    { id: "create", label: "Create", active: true, onSelect: () => navigate("/support/create-organization") },
  ]);

  // ---- Form ----
  const [orgName, setOrgName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [memberEmails, setMemberEmails] = useState<string[]>([]);
  const [memberInput, setMemberInput] = useState("");
  const [role, setRole] = useState("Member");
  const [domain, setDomain] = useState("");
  const [createDemoData, setCreateDemoData] = useState(true);

  // ---- Run ----
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<{ org: CreateOrgResult; password: string } | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<BulkInviteResult | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [domainSet, setDomainSet] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const adminEmailTrim = adminEmail.trim().toLowerCase();
  const domainTrim = domain.trim();
  const domainInvalid = !isAcceptableDomain(domain); // blank is acceptable (means "don't set one")
  // Fold any half-typed address in the member input into the batch, and never invite the admin to their own org.
  const invitees = Array.from(new Set([...memberEmails, ...splitEmails(memberInput)])).filter((e) => e !== adminEmailTrim);
  const badInvitees = invitees.filter((e) => !EMAIL_RE.test(e));

  const formValid =
    orgName.trim() !== "" &&
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    EMAIL_RE.test(adminEmailTrim) &&
    badInvitees.length === 0 &&
    !domainInvalid;

  async function copyToClipboard(password: string) {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      const sel = window.getSelection();
      const node = document.getElementById("org-admin-password-value");
      if (sel && node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand("copy");
        setCopied(true);
      }
    }
  }

  async function run() {
    setRunning(true);
    setRunError(null);
    const password = generatePassword();

    let org: CreateOrgResult;
    try {
      org = await api.createOrganizationForCustomer({
        organizationName: orgName.trim(),
        email: adminEmailTrim,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        createDemoData,
      });
    } catch (err) {
      setRunError(err instanceof ApiError ? err.message : `${err}`);
      setRunning(false);
      return;
    }
    setResult({ org, password });
    setConfirming(false);

    // The org exists from here on — the follow-ups are best-effort and reported, never fatal.
    // Sales handed the admin their password, so an email-verification loop is pure friction: skip it.
    try {
      await api.forceVerifyEmail(org.userId);
    } catch (err) {
      setVerifyError(err instanceof ApiError ? err.message : `${err}`);
    }

    if (invitees.length > 0) {
      try {
        setInviteResult(await api.bulkInviteToOrganization(org.organizationId, { emails: invitees, role }));
      } catch (err) {
        setInviteError(err instanceof ApiError ? err.message : `${err}`);
      }
    }

    // Domain last: setting it earlier would block the admin's own registration (verified domains
    // are barred from self-registering — that's the point of the field).
    if (domainTrim) {
      try {
        await api.setOrganizationVerifiedDomain(org.organizationId, domainTrim);
        setDomainSet(domainTrim);
      } catch (err) {
        setDomainError(err instanceof ApiError ? err.message : `${err}`);
      }
    }

    setRunning(false);
  }

  function reset() {
    setOrgName(""); setFirstName(""); setLastName(""); setAdminEmail("");
    setMemberEmails([]); setMemberInput(""); setRole("Member"); setDomain(""); setCreateDemoData(false);
    setConfirming(false); setRunning(false); setRunError(null);
    setResult(null); setVerifyError(null); setInviteResult(null); setInviteError(null);
    setDomainError(null); setDomainSet(null); setCopied(false);
  }

  const inviteCounts = inviteResult && {
    invited: inviteResult.results.filter((r) => r.status === "invited").length,
    skipped: inviteResult.results.filter((r) => r.status === "skipped").length,
    error: inviteResult.results.filter((r) => r.status === "error").length,
  };

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Organizations · Create</div>
        <h1>Create organization</h1>
        <p>
          Set up an organization for a customer — for sales-led deals where self-registration keeps going
          wrong. Creates the org with the person below as its Administrator (you'll get their generated
          password to send back), verifies their email, and invites the rest of their team.
        </p>
      </div>

      {!result && (
        <>
          <section className="panel reveal" data-d="2">
            <header>
              <span className="idx">01</span> Organization
              <span className="hint">the customer's company</span>
            </header>
            <div className="body">
              <div className="field">
                <label>Organization name <span className="req">*</span></label>
                <input
                  type="text"
                  placeholder="Acme Construction"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Verified domain <span className="opt">— optional</span></label>
                <input
                  type="text"
                  placeholder="company.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                />
                <div className="hint">
                  When set, anyone with this email domain is blocked from self-registering a new organization —
                  they must be invited. Applied last, after the admin and invites are created.
                </div>
                {domainInvalid && (
                  <div className="hint" style={{ color: "var(--red)", marginTop: 5 }}>
                    Enter a bare domain like <span className="mono">company.com</span> (no https://, path or spaces).
                  </div>
                )}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={createDemoData}
                  onChange={(e) => setCreateDemoData(e.target.checked)}
                  style={{ width: "auto" }}
                />
                Create the demo project in their workspace
              </label>
            </div>
          </section>

          <section className="panel reveal" data-d="3">
            <header>
              <span className="idx">02</span> Organization admin
              <span className="hint">becomes the org's Administrator</span>
            </header>
            <div className="body">
              <div style={{ display: "flex", gap: 14 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label>First name <span className="req">*</span></label>
                  <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} spellCheck={false} autoComplete="off" />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>Last name <span className="req">*</span></label>
                  <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} spellCheck={false} autoComplete="off" />
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Email <span className="req">*</span></label>
                <input
                  type="text"
                  placeholder="owner@company.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                />
                <div className="hint">
                  Must not already have an account. A strong password is generated for them and shown to you
                  once after creation — send it back to them; they should change it after first sign-in.
                </div>
              </div>
            </div>
          </section>

          <section className="panel reveal" data-d="4">
            <header>
              <span className="idx">03</span> Team members
              <span className="hint">optional — invited on the admin's behalf</span>
            </header>
            <div className="body">
              <EmailChipsInput emails={memberEmails} onEmailsChange={setMemberEmails} input={memberInput} onInputChange={setMemberInput} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
                <div className="rail-label">Role</div>
                <select value={role} onChange={(e) => setRole(e.target.value)} style={{ width: "auto", fontSize: 12, padding: "7px 28px 7px 10px" }}>
                  <option value="Member">Member</option>
                  <option value="Administrator">Administrator</option>
                </select>
              </div>
              <div className="hint" style={{ marginTop: 10 }}>
                Each person gets a normal invitation email (attributed to the new org admin) and sets their own
                password when they accept.
              </div>
            </div>
          </section>

          <div className="reveal" data-d="4" style={{ marginTop: 22 }}>
            {!confirming ? (
              <div className="btn-row">
                <button className="btn btn-primary" disabled={!formValid} onClick={() => setConfirming(true)}>
                  <span className="glyph" /> Create organization
                </button>
                {invitees.length > 0 && (
                  <span className="faint mono" style={{ fontSize: 11 }}>
                    + {invitees.length} invite{invitees.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            ) : (
              <div className="callout warn">
                <span className="mk">confirm</span>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 12 }}>
                    Create organization <b className="mono">{orgName.trim()}</b> with{" "}
                    <b className="mono">{adminEmailTrim}</b> as its Administrator
                    {invitees.length > 0 && <> and invite <b>{invitees.length}</b> team member{invitees.length === 1 ? "" : "s"} as {role}</>}
                    {domainTrim && <>, then lock the domain <b className="mono">{domainTrim}</b></>}
                    ? This creates a real account and a ChargeBee subscription.
                  </div>
                  {runError && (
                    <div className="callout error" style={{ marginBottom: 12 }}>
                      <span className="mk">err</span>
                      <span>{runError}</span>
                    </div>
                  )}
                  <div className="btn-row">
                    <button className={"btn btn-primary" + (running ? " loading" : "")} disabled={running} onClick={() => void run()}>
                      <span className="glyph" /> Confirm &amp; create
                    </button>
                    <button className="btn btn-ghost" disabled={running} onClick={() => setConfirming(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {result && (
        <>
          <section className="panel reveal" data-d="1" style={{ marginTop: 22 }}>
            <header>
              <span className="idx" style={{ color: "var(--lime)" }}>✓</span> Organization created
              <span className="hint">{running ? "finishing up…" : "done"}</span>
            </header>
            <div className="body">
              <div className="callout warn" style={{ marginBottom: 16 }}>
                <span className="mk">note</span>
                <span>
                  This password is shown <b>only here</b> — copy it and send it to{" "}
                  <b className="mono">{adminEmailTrim}</b>. They should change it after
                  first sign-in. Their email is already verified, so they can sign in straight away.
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  border: "1px solid var(--line)",
                  background: "var(--bg-panel-2)",
                  marginBottom: 16,
                }}
              >
                <code
                  id="org-admin-password-value"
                  className="mono"
                  style={{ flex: 1, fontSize: 16, letterSpacing: "0.06em", userSelect: "all" }}
                >
                  {result.password}
                </code>
                <button type="button" className="btn btn-sm" onClick={() => void copyToClipboard(result.password)}>
                  <span className="glyph" /> {copied ? "Copied" : "Copy"}
                </button>
              </div>

              <dl className="spec">
                <dt>organization</dt>
                <dd>{result.org.organizationName}</dd>
                <dt>organization id</dt>
                <dd><CopyableId value={result.org.organizationId} /></dd>
                <dt>admin</dt>
                <dd className="mono">{adminEmailTrim}</dd>
                <dt>admin user id</dt>
                <dd><CopyableId value={result.org.userId} /></dd>
                {result.org.workspaceId && (
                  <>
                    <dt>default workspace</dt>
                    <dd><CopyableId value={result.org.workspaceId} /></dd>
                  </>
                )}
                {domainSet && (
                  <>
                    <dt>verified domain</dt>
                    <dd className="mono">{domainSet}</dd>
                  </>
                )}
              </dl>

              {verifyError && (
                <div className="callout warn" style={{ marginTop: 16 }}>
                  <span className="mk">note</span>
                  <span>
                    Email verification failed ({verifyError}) — verify them manually from the Users page, or
                    they'll be asked to verify on first sign-in.
                  </span>
                </div>
              )}
              {inviteError && (
                <div className="callout error" style={{ marginTop: 16 }}>
                  <span className="mk">err</span>
                  <span>
                    Inviting the team failed: {inviteError}. Open the organization and use “Invite users” to retry.
                  </span>
                </div>
              )}
              {domainError && (
                <div className="callout warn" style={{ marginTop: 16 }}>
                  <span className="mk">note</span>
                  <span>
                    Setting the verified domain failed: {domainError}. You can set it from the organization's
                    “Set Domain” dialog.
                  </span>
                </div>
              )}

              {inviteResult && inviteCounts && (
                <>
                  <div className="callout" style={{ marginTop: 16 }}>
                    <span className="mk">ok</span>
                    <span style={{ fontSize: 11 }}>
                      {inviteCounts.invited} invited, {inviteCounts.skipped} skipped, {inviteCounts.error} error
                    </span>
                  </div>
                  <dl className="spec spec-kv" style={{ fontSize: 11, marginTop: 10 }}>
                    {inviteResult.results.map((r) => (
                      <Fragment key={r.email}>
                        <dt className="mono" title={r.email}>{r.email}</dt>
                        <dd className="mono" style={{ color: r.status === "invited" ? "var(--lime)" : r.status === "error" ? "var(--red)" : undefined }}>
                          {r.status}{r.error ? ` — ${r.error}` : ""}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </>
              )}

              <div className="btn-row" style={{ marginTop: 18 }}>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/support/organizations?org=${encodeURIComponent(result.org.organizationId)}`)}
                >
                  <span className="glyph" /> Open organization
                </button>
                <button className="btn" onClick={reset}>
                  <span className="glyph" /> Create another
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
